import React, { useRef, useState } from 'react'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import { useStaticQuery, graphql } from 'gatsby'
import Img, { FluidObject } from 'gatsby-image'

import { Container } from 'components/Container'
import { backgroundColors, colors } from 'styles/colors'
import { displayWidth } from 'styles/width'
import { mobileAfterBorder } from 'styles/mobileAfterBorder'
import { Title, TitleH1 } from 'components/TitleComponent'
import { getDataByLanguage } from 'utils/getDataByLanguage'
import { SliderComponent } from 'components/SliderComponent'
import { indent } from 'styles/indent'
import FullScreen from 'assets/icons/fullScreen.svg'
import { ModalCarousel } from 'components/ModalCarousel'
import { ProjectImages } from 'layout/Project'
import { sendEvent } from 'tracking'
import { sendForm } from 'components/form/api'
import { headerBg } from 'styles/headerBg'
import { LeftSidebar, RightSidebar } from 'components/SideBar'
import { Button } from 'components/Button'
import Slider from 'react-slick'

const ExampleOfProjectWrapper = styled.div`
    display: flex;
    justify-content: center;
    width: 100%;
    background-color: ${backgroundColors.contact};
    position: relative;
    border-bottom: 1px solid ${colors.dark};
    ${mobileAfterBorder}
    :before {
        ${headerBg}
    }
`

const SubTitle = styled.p`
    font-weight: normal;
    font-size: 16px;
    line-height: 26px;
    text-align: center;
    letter-spacing: 0.4px;
    color: ${colors.dark};
    margin-bottom: 32px;
    @media (min-width: ${displayWidth.tablet}) {
        text-align: left;
    }
`

const TitleStyledH1 = styled(TitleH1)`
    margin-top: 8px;
    @media (min-width: ${displayWidth.tablet}) {
        margin-top: 32px;
        margin-left: 0;
        margin-right: 0;
    }
`

const ButtonStyled = styled(Button)`
    a {
        text-decoration: none;
        color: #fff;
        font-size: 18px;
    }
    @media (min-width: ${displayWidth.tablet}) {
        margin: 30px 0;
    }
`

const TitleStyled = styled(Title)`
    @media (min-width: ${displayWidth.tablet}) {
        margin-left: 0;
        margin-right: 0;
    }
`

const HeroColumn = styled.div`
    display: flex;
    flex-direction: column;
    padding: 0 ${indent.heroColumnTablet} 24px;
    border-bottom: 1px solid ${colors.dark};
    @media (min-width: ${displayWidth.tablet}) {
        border-bottom: none;
        border-right: 1px solid ${colors.dark};
    }
    @media (min-width: ${displayWidth.desktop}) {
        padding: 0 ${indent.heroColumnDesktop} 24px;
    }
`

const ImgStyled = styled(Img)<{ fluid: FluidObject }>`
    width: 100%;
    height: 100%;
    max-height: 600px;
    img {
        max-height: 600px;
        object-fit: contain;
    }
`

const Wrapper = styled.div`
    display: flex;
    align-items: center;
    background-color: ${colors.white};
    position: relative;
    overflow-x: hidden;
`

const FullScreenButton = styled(FullScreen)`
    fill: ${colors.white};
    stroke: ${colors.dark};
    stroke-width: 1px;
    position: absolute;
    top: 10px;
    right: 26px;
    width: 50px;
    height: 50px;
    cursor: pointer;
    z-index: 3;
    display: none;
    @media (min-width: ${displayWidth.tablet}) {
        display: block;
    }
    @media (min-width: ${displayWidth.desktop}) {
        right: 10px;
    }
`

export const ExampleOfProjectGenPlan = ({
    withH2Header = false,
}: {
    withH2Header?: boolean
}) => {
    const { i18n } = useTranslation()
    const [isModalOpen, setModalIsOpen] = useState(false)
    const [currentSlideS, setCurrentSlideS] = useState(0)
    const sliderRefD = useRef<Slider | null>(null)
    const sliderD = sliderRefD.current

    const data = useStaticQuery(graphql`
        query {
            genplanImages: allFile(
                filter: { relativeDirectory: { eq: "generalPlan" } }
                sort: { fields: name }
            ) {
                edges {
                    node {
                        id
                        childImageSharp {
                            fluid(
                                srcSetBreakpoints: [800, 1600]
                                quality: 100
                            ) {
                                ...GatsbyImageSharpFluid
                            }
                            parent {
                                ... on File {
                                    name
                                }
                            }
                        }
                    }
                }
            }
            allGenplanImagesDataYaml {
                edges {
                    node {
                        title
                        description
                        downloadText
                        compositionTitle
                        items {
                            title
                            alt
                        }
                        parent {
                            ... on File {
                                name
                            }
                        }
                    }
                }
            }
        }
    `)

    const genplanData = getDataByLanguage(
        data.allGenplanImagesDataYaml,
        i18n.language
    )
    const { title, description, downloadText } = genplanData

    const sliderSettings = {
        infinite: true,
        responsive: [
            {
                breakpoint: 1024,
                settings: {
                    arrows: false,
                    dots: true,
                },
            },
        ],
    }

    const images = data.genplanImages.edges.filter(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ({ node }: any) => node.childImageSharp !== null
    )

    return (
        <ExampleOfProjectWrapper>
            <LeftSidebar background={backgroundColors.contact} />
            <Container columns={'1fr'} tabletColumns={'1fr 2fr'}>
                <HeroColumn>
                    {withH2Header ? (
                        <TitleStyled>{title}</TitleStyled>
                    ) : (
                        <TitleStyledH1>{title}</TitleStyledH1>
                    )}
                    <SubTitle>{description}</SubTitle>
                    {downloadText && (
                        <ButtonStyled
                            onClick={() => {
                                sendForm(`Скачали проект GenPlan`, {})
                                sendEvent('Click', {
                                    eventCategory: 'DownloadExampleGenPlan',
                                    placement: 'ExampleOfProjectGenPlan',
                                })
                            }}
                            id="DownloadExampleGenPlan"
                        >
                            <a
                                href="https://clearline.com.ua/general-site-plan.pdf"
                                download
                            >
                                {downloadText}
                            </a>
                        </ButtonStyled>
                    )}
                </HeroColumn>
                <Wrapper>
                    <FullScreenButton
                        onClick={() => {
                            sendEvent('FullScreen', {
                                eventCategory: 'SliderExampleGenPlan',
                                placement: 'ExampleOfProjectGenPlan',
                            })
                            setModalIsOpen(true)
                        }}
                    />
                    <SliderComponent
                        {...sliderSettings}
                        forwardRef={sliderRefD}
                        afterChange={(current: number) => {
                            setCurrentSlideS(current)
                            sendEvent('ShowSlide', {
                                eventCategory: 'SliderExampleGenPlan',
                                currentSlide: `${current}`,
                                placement: 'ExampleOfProjectGenPlan',
                            })
                        }}
                    >
                        {images.map(
                            (
                                item: {
                                    node: {
                                        childImageSharp: {
                                            fluid: FluidObject
                                            parent: { name: string }
                                        }
                                    }
                                },
                                index: number
                            ) => (
                                <ImgStyled
                                    key={index}
                                    fluid={item.node.childImageSharp.fluid}
                                    imgStyle={{ objectFit: 'contain' }}
                                    alt={genplanData.items[index]?.alt}
                                    title={genplanData.items[index]?.title}
                                />
                            )
                        )}
                    </SliderComponent>
                </Wrapper>
            </Container>
            <RightSidebar />
            <ModalCarousel
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                data={images.map(({ node }: any) => node as ProjectImages)}
                setCurrentSlideS={setCurrentSlideS}
                isModalOpen={isModalOpen}
                closeHandler={() => {
                    setModalIsOpen(false)
                    sliderD !== null && sliderD.slickGoTo(currentSlideS)
                }}
                initialSlideIndex={currentSlideS}
            />
        </ExampleOfProjectWrapper>
    )
}
